import { useState, type ReactNode } from "react";

// Two steps for anything that writes. Step one is a plain button. Step two is
// the strip: what will happen, the confirm button, Cancel, and the note.

type StripProps = {
  confirmLabel: string;
  onConfirm: () => void | Promise<unknown>;
  onCancel: () => void;
  note?: ReactNode;
  busy?: boolean;
  children?: ReactNode;
};

export function ConfirmStrip({ confirmLabel, onConfirm, onCancel, note, busy, children }: StripProps) {
  return (
    <div className="confirm" role="group">
      {children ? <div className="cbody">{children}</div> : null}
      <button type="button" className="btn" disabled={busy} onClick={() => void onConfirm()}>
        {busy ? "Working" : confirmLabel}
      </button>
      <button type="button" className="btn quiet" disabled={busy} onClick={onCancel}>
        Cancel
      </button>
      {note ? <p className="cnote">{note}</p> : null}
    </div>
  );
}

type BarProps = {
  label: string;
  confirmLabel: string;
  note?: ReactNode;
  /** Runs when the bar arms, for a preview call whose result renders as children. */
  onArm?: () => void | Promise<unknown>;
  onCancel?: () => void;
  onConfirm: () => void | Promise<unknown>;
  disabled?: boolean;
  busy?: boolean;
  small?: boolean;
  title?: string;
  children?: ReactNode;
};

export function ConfirmBar({ label, confirmLabel, note, onArm, onCancel, onConfirm, disabled, busy, small, title, children }: BarProps) {
  const [armed, setArmed] = useState(false);

  if (!armed) {
    return (
      <button
        type="button"
        className={`btn${small ? " sm" : ""}`}
        disabled={disabled || busy}
        title={title}
        onClick={() => {
          setArmed(true);
          void onArm?.();
        }}
      >
        {label}
      </button>
    );
  }

  return (
    <ConfirmStrip
      confirmLabel={confirmLabel}
      note={note}
      busy={busy}
      onCancel={() => {
        setArmed(false);
        onCancel?.();
      }}
      onConfirm={async () => {
        try {
          await onConfirm();
        } finally {
          setArmed(false);
        }
      }}
    >
      {children}
    </ConfirmStrip>
  );
}
